import React, { useEffect, useMemo, useState } from "react";
import api, { formatTL } from "@/lib/api";
import { toast } from "sonner";
import ProductEditor, { EMPTY_PRODUCT, STOCK_OPTS } from "@/pages/admin/ProductEditor";
import {
  Folder, FolderOpen, Search, Plus, Edit, ChevronRight, ChevronDown,
  Image as ImageIcon, Loader2, Eye, EyeOff, X, AlertTriangle, RefreshCw,
} from "lucide-react";

const STATUS_OPTS = [
  { v: "published", l: "Yayında", c: "bg-[#16A34A]" },
  { v: "draft", l: "Taslak", c: "bg-[#71717A]" },
];

const LIMIT = 50;
const INITIAL_FILTERS = { category_id: "", q: "", brand: "", status: "", stock_status: "", min_price: "", max_price: "", sort: "newest" };

export default function ProductsCatalog() {
  const [cats, setCats] = useState([]);
  const [counts, setCounts] = useState({});
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(0);

  const reloadCats = async () => {
    const cRes = await api.get("/categories");
    const cntRes = await api.get("/categories/counts?status=");
    setCats(cRes.data);
    setCounts(cntRes.data);
  };

  const reload = async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.category_id) qs.set("category_id", filters.category_id);
    if (filters.q) qs.set("q", filters.q);
    qs.set("status", filters.status);
    qs.set("sort", filters.sort);
    qs.set("limit", String(LIMIT));
    qs.set("skip", String(page * LIMIT));
    try {
      const res = await api.get("/products?" + qs.toString());
      let items = res.data.items || [];
      if (filters.brand) items = items.filter((p) => (p.brand || "").toLowerCase().includes(filters.brand.toLowerCase()));
      if (filters.stock_status) items = items.filter((p) => (p.stock_status || "in_stock") === filters.stock_status);
      if (filters.min_price) items = items.filter((p) => (p.price || 0) >= parseFloat(filters.min_price));
      if (filters.max_price) items = items.filter((p) => (p.price || 0) <= parseFloat(filters.max_price));
      setProducts(items);
      setTotal(res.data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { reloadCats(); }, []);
  useEffect(() => { reload(); }, [filters, page]);

  // Build category lookup
  const byId = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const childrenMap = useMemo(() => {
    const m = {};
    cats.forEach((c) => {
      const pid = c.parent_id || "__root__";
      m[pid] = m[pid] || [];
      m[pid].push(c);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
    return m;
  }, [cats]);

  // Flatten tree to a list of rows iteratively (avoids recursive component)
  const treeRows = useMemo(() => buildTreeRows(childrenMap, expanded), [childrenMap, expanded]);

  const brandOptions = useMemo(() => {
    const s = new Set();
    products.forEach((p) => { if (p.brand) s.add(p.brand); });
    return [...s].sort();
  }, [products]);

  const categoryName = (id) => {
    if (!id) return "—";
    const path = [];
    let cur = byId[id];
    while (cur) { path.unshift(cur.name_tr); cur = cur.parent_id ? byId[cur.parent_id] : null; }
    return path.join(" / ");
  };

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const allCheckedOnPage = products.length > 0 && products.every((p) => selected[p.id]);

  const toggleAllOnPage = () => {
    const next = { ...selected };
    if (allCheckedOnPage) {
      products.forEach((p) => { delete next[p.id]; });
    } else {
      products.forEach((p) => { next[p.id] = true; });
    }
    setSelected(next);
  };

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); };

  const bulk = async (action, extra) => {
    if (!selIds.length) { toast.error("Hiç ürün seçili değil"); return; }
    if (action === "delete" && !window.confirm(selIds.length + " ürün silinecek. Emin misin?")) return;
    try {
      const payload = { action, product_ids: selIds, ...(extra || {}) };
      const res = await api.post("/admin/products/bulk", payload);
      toast.success(res.data.affected + " ürün işlendi");
      setSelected({});
      reload(); reloadCats();
    } catch (e) {
      toast.error("Toplu işlem hatası: " + (e?.response?.data?.detail || e.message));
    }
  };

  const inlinePatch = async (pid, patch) => {
    try {
      await api.patch(`/products/${pid}/inline`, patch);
      setProducts((arr) => arr.map((p) => p.id === pid ? { ...p, ...patch } : p));
      toast.success("Güncellendi");
    } catch (e) { toast.error("Güncelleme hatası"); }
  };

  const hasAnyFilter = !!(filters.q || filters.brand || filters.status || filters.stock_status || filters.min_price || filters.max_price || filters.category_id);

  const [dedupeBusy, setDedupeBusy] = useState(false);
  const runDedupe = async () => {
    if (!window.confirm("Kategori ağacını temizleyip duplikatları birleştirir. Devam edilsin mi?")) return;
    setDedupeBusy(true);
    try {
      const res = await api.post("/admin/categories/dedupe-merge");
      const r = res.data?.report || {};
      const moved = r.products_moved || 0;
      const orphans = (r.orphan_brands_removed || []).length;
      const subs = (r.subcategories_moved || []).length;
      toast.success(`Tamamlandı · ${orphans} marka birleştirildi · ${subs} alt kategori taşındı · ${moved} ürün yer değiştirdi`);
      reloadCats(); reload();
    } catch (e) {
      toast.error("Kategori temizleme hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setDedupeBusy(false); }
  };

  // ===== Category bulk publish / unpublish =====
  const [bulkStatusModal, setBulkStatusModal] = useState(null); // { targetStatus, preview, busy }

  const openBulkStatus = async (targetStatus) => {
    const cid = filters.category_id || "__all__";
    const catLabel = filters.category_id ? categoryName(filters.category_id) : "Tüm Ürünler";
    setBulkStatusModal({ targetStatus, catLabel, cid, preview: null, busy: true, confirming: false });
    try {
      const res = await api.post(`/admin/categories/${cid}/bulk-status`, { status: targetStatus, dry_run: true });
      setBulkStatusModal((m) => m ? { ...m, preview: res.data, busy: false } : null);
    } catch (e) {
      toast.error("Önizleme hatası: " + (e?.response?.data?.detail || e.message));
      setBulkStatusModal(null);
    }
  };

  const applyBulkStatus = async () => {
    if (!bulkStatusModal || !bulkStatusModal.preview) return;
    setBulkStatusModal((m) => ({ ...m, confirming: true }));
    try {
      const { cid, targetStatus, preview } = bulkStatusModal;
      const res = await api.post(`/admin/categories/${cid}/bulk-status`, { status: targetStatus });
      const verb = targetStatus === "published" ? "yayınlandı" : "taslağa alındı";
      toast.success(`${res.data.modified || 0} ürün ${verb} (${preview.matched} kapsamda)`);
      setBulkStatusModal(null);
      reload(); reloadCats();
    } catch (e) {
      toast.error("Toplu durum hatası: " + (e?.response?.data?.detail || e.message));
      setBulkStatusModal((m) => m ? { ...m, confirming: false } : null);
    }
  };

  // ===== Gallery integrity =====
  const [galleryModal, setGalleryModal] = useState(null); // {mode:'audit'|'reset', preview, busy, confirming}

  const openGalleryAction = async (mode) => {
    const cid = filters.category_id || "__all__";
    const catLabel = filters.category_id ? categoryName(filters.category_id) : "Tüm Ürünler";
    setGalleryModal({ mode, cid, catLabel, preview: null, busy: true, confirming: false });
    try {
      if (mode === "audit") {
        const res = await api.post("/admin/products/images/audit",
          { category_id: filters.category_id || null, threshold: 3 },
          { timeout: 180000 });
        setGalleryModal((m) => m ? { ...m, preview: res.data, busy: false } : null);
      } else if (mode === "reset") {
        const res = await api.post("/admin/products/images/reset-for-reimport",
          { category_id: filters.category_id || "__all__", dry_run: true, unpublish: true });
        setGalleryModal((m) => m ? { ...m, preview: res.data, busy: false } : null);
      }
    } catch (e) {
      toast.error("Hata: " + (e?.response?.data?.detail || e.message));
      setGalleryModal(null);
    }
  };

  const applyGalleryReset = async () => {
    if (!galleryModal) return;
    setGalleryModal((m) => ({ ...m, confirming: true }));
    try {
      const res = await api.post("/admin/products/images/reset-for-reimport",
        { category_id: filters.category_id || "__all__", dry_run: false, unpublish: true });
      toast.success(`${res.data.modified || 0} ürün sıfırlandı. Yeni import için hazır.`);
      setGalleryModal(null);
      reload();
    } catch (e) {
      toast.error("Sıfırlama hatası: " + (e?.response?.data?.detail || e.message));
      setGalleryModal((m) => m ? { ...m, confirming: false } : null);
    }
  };

  return (
    <div data-testid="products-catalog" className="flex flex-col h-[calc(100vh-120px)] -mx-2">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl">Ürün Kataloğu</h1>
          <p className="text-xs text-[#71717A] mt-0.5">{total} ürün · {selIds.length} seçili</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="bulk-publish-btn"
            onClick={() => openBulkStatus("published")}
            className="border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <Eye size={14} /> Tümünü Yayınla
          </button>
          <button data-testid="bulk-unpublish-btn"
            onClick={() => openBulkStatus("draft")}
            className="border border-[#71717A] text-[#52525B] hover:bg-[#52525B] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <EyeOff size={14} /> Tümünü Taslağa Al
          </button>
          <button data-testid="dedupe-categories-btn"
            onClick={runDedupe} disabled={dedupeBusy}
            className="border border-[#C8A96A] text-[#A6864A] hover:bg-[#C8A96A] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50">
            {dedupeBusy ? <Loader2 size={14} className="animate-spin" /> : <FolderOpen size={14} />}
            Kategori Temizle ve Birleştir
          </button>
          <button data-testid="gallery-audit-btn"
            onClick={() => openGalleryAction("audit")}
            className="border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <ImageIcon size={14} /> Galeri Denetle
          </button>
          <button data-testid="gallery-reset-btn"
            onClick={() => openGalleryAction("reset")}
            className="border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <RefreshCw size={14} /> Yeniden İçe Aktarmaya Hazırla
          </button>
          <button data-testid="new-product-btn"
            onClick={() => { setCreating(true); setEditing(EMPTY_PRODUCT); }}
            className="bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
            <Plus size={14} /> Yeni Ürün
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden min-h-0">
        <aside className="w-64 border-r border-border overflow-y-auto py-3 px-2 bg-[#FAF8F3] shrink-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] px-2 mb-2">Kategoriler</div>
          <button onClick={() => setFilter("category_id", "")} data-testid="cat-pick-all"
            className={"flex items-center w-full px-2 py-1.5 text-xs text-left hover:bg-white " + (!filters.category_id ? "bg-white text-[#C8A96A] font-medium" : "text-[#52525B]")}>
            <Folder size={12} className="mr-1.5" /> Tümü
          </button>
          {treeRows.map((row) => (
            <CatRow key={row.cat.id} row={row} active={filters.category_id} counts={counts}
              expanded={expanded} setExpanded={setExpanded}
              onPick={(id) => setFilter("category_id", id)} />
          ))}
        </aside>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border bg-white flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input data-testid="catalog-search" value={filters.q} onChange={(e) => setFilter("q", e.target.value)}
                placeholder="Ad / SKU / Marka..." className="w-full pl-8 pr-3 py-2 border border-border text-sm focus:outline-none focus:border-[#C8A96A]" />
            </div>
            <SelectInput testid="filter-brand" value={filters.brand} onChange={(v) => setFilter("brand", v)}
              options={brandOptionsFor(brandOptions)} />
            <SelectInput testid="filter-status" value={filters.status} onChange={(v) => setFilter("status", v)}
              options={[["", "Tüm Durumlar"], ["published", "Yayında"], ["draft", "Taslak"]]} />
            <SelectInput testid="filter-stock" value={filters.stock_status} onChange={(v) => setFilter("stock_status", v)}
              options={stockFilterOpts()} />
            <div className="flex items-center gap-1 border border-border px-2 py-1.5 text-xs">
              <input data-testid="filter-min-price" type="number" placeholder="Min ₺" value={filters.min_price}
                onChange={(e) => setFilter("min_price", e.target.value)} className="w-20 focus:outline-none" />
              <span className="text-[#71717A]">–</span>
              <input data-testid="filter-max-price" type="number" placeholder="Max ₺" value={filters.max_price}
                onChange={(e) => setFilter("max_price", e.target.value)} className="w-20 focus:outline-none" />
            </div>
            <SelectInput testid="filter-sort" value={filters.sort} onChange={(v) => setFilter("sort", v)}
              options={[["newest", "En Yeni"], ["price_asc", "Fiyat: Artan"], ["price_desc", "Fiyat: Azalan"], ["name", "İsim"]]} />
            {hasAnyFilter && (
              <button onClick={() => { setFilters(INITIAL_FILTERS); setPage(0); }} className="text-[#EF4444] text-xs underline">Temizle</button>
            )}
          </div>

          {selIds.length > 0 && <BulkBar count={selIds.length} cats={cats} onBulk={bulk} onClear={() => setSelected({})} />}

          {loading ? (
            <div className="text-center py-20 text-sm text-[#71717A]"><Loader2 className="inline animate-spin mr-2" size={14} /> Yükleniyor…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 text-sm text-[#71717A]">Bu filtrelere uyan ürün yok</div>
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-border z-10">
                  <tr className="text-left">
                    <th className="p-3 w-10"><input type="checkbox" checked={allCheckedOnPage} onChange={toggleAllOnPage} data-testid="catalog-select-all" /></th>
                    <th className="p-3 w-16">Görsel</th>
                    <th className="p-3">Ürün Adı</th>
                    <th className="p-3 w-32">SKU</th>
                    <th className="p-3">Kategori</th>
                    <th className="p-3 w-24 text-right">Fiyat</th>
                    <th className="p-3 w-16 text-center">Stok</th>
                    <th className="p-3 w-28">Stok Durumu</th>
                    <th className="p-3 w-24">Yayın</th>
                    <th className="p-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <ProductRow key={p.id} p={p} categoryName={categoryName(p.category_id)}
                      selected={!!selected[p.id]}
                      onToggle={() => setSelected({ ...selected, [p.id]: !selected[p.id] })}
                      onEdit={() => { setCreating(false); setEditing(p); }}
                      onPatch={inlinePatch} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > LIMIT && (
            <div className="px-4 py-2 border-t border-border flex items-center justify-between text-xs">
              <div className="text-[#71717A]">{page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} / {total}</div>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                  className="border border-border px-3 py-1 disabled:opacity-30">‹ Önceki</button>
                <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * LIMIT >= total}
                  className="border border-border px-3 py-1 disabled:opacity-30">Sonraki ›</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ProductEditor
          product={creating ? null : editing}
          initial={editing}
          cats={cats}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); reloadCats(); }}
        />
      )}

      {bulkStatusModal && (
        <BulkStatusModal modal={bulkStatusModal} onCancel={() => setBulkStatusModal(null)} onApply={applyBulkStatus} />
      )}

      {galleryModal && (
        <GalleryModal modal={galleryModal} onCancel={() => setGalleryModal(null)} onApply={applyGalleryReset} />
      )}
    </div>
  );
}

function GalleryModal({ modal, onCancel, onApply }) {
  const { mode, catLabel, preview, busy, confirming } = modal;
  const isAudit = mode === "audit";
  const headerColor = isAudit ? "#3B82F6" : "#DC2626";
  return (
    <div data-testid="gallery-modal" className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between" style={{ background: headerColor + "10" }}>
          <div className="flex items-center gap-2">
            {isAudit ? <ImageIcon size={16} style={{ color: headerColor }} /> : <AlertTriangle size={16} style={{ color: headerColor }} />}
            <h3 className="font-heading text-lg" style={{ color: headerColor }}>
              {isAudit ? "Galeri Denetimi" : "Yeniden İçe Aktarmaya Hazırla"}
            </h3>
          </div>
          <button onClick={onCancel} className="p-1 hover:bg-white/40"><X size={16} /></button>
        </div>
        <div className="p-5 text-sm">
          <p className="text-[#52525B] mb-1">Kapsam</p>
          <p data-testid="gallery-modal-scope" className="font-medium text-base mb-4">{catLabel}</p>
          {busy ? (
            <div className="text-[#71717A] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />
              {isAudit ? "Tüm görseller içerikleri karşılaştırılıyor — bu işlem birkaç dakika sürebilir…" : "Etki hesaplanıyor…"}
            </div>
          ) : !preview ? null : isAudit ? (
            <div className="space-y-2">
              <Row label="Kapsamdaki toplam ürün" value={preview.total_products_in_scope ?? preview.total_products} />
              <Row label="İncelenen ürün (örnek)" value={preview.total_products} />
              {preview.sampled && (
                <p className="text-[11px] text-[#A6864A] italic">Performans için ilk {preview.total_products} ürün üzerinden örnek denetim yapıldı. Tam denetim için kategori bazında daraltın.</p>
              )}
              <Row label="İncelenen görsel" value={preview.total_images_inspected} />
              <Row label="Farklı içerik (hash)" value={preview.distinct_hashes} />
              <Row label="Paylaşılan içerik (>=3 üründe aynı)" value={preview.shared_hashes} bold color={preview.shared_hashes > 0 ? "#DC2626" : "#16A34A"} />
              <Row label="Etkilenen ürün" value={preview.affected_products} bold color={preview.affected_products > 0 ? "#DC2626" : "#16A34A"} />
              <Row label="Etkilenen görsel referansı" value={preview.affected_image_refs} />
              {preview.shared_hashes === 0 && (
                <p className="text-xs text-[#16A34A] mt-2">✓ Galeri verisi temiz — düzeltme gerekmiyor.</p>
              )}
              {preview.shared_hashes > 0 && (
                <p className="text-xs text-[#71717A] mt-2 italic">
                  Düzeltmek için &quot;Yeniden İçe Aktarmaya Hazırla&quot; butonunu kullanın → ardından yeniden import çalıştırın.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Row label="Kapsamdaki toplam ürün" value={preview.matched} />
              <Row label="Görseli olan ürün" value={preview.with_images} />
              <Row label="Şu an Yayında" value={preview.published} />
              <div className="border-t border-border pt-2 mt-2">
                <Row label="Görseli sıfırlanacak ürün" value={preview.will_clear_images} bold color="#DC2626" />
                <Row label="Taslağa alınacak ürün" value={preview.will_unpublish} bold color="#DC2626" />
              </div>
              <p className="text-xs text-[#71717A] mt-2 italic">
                Bu işlem geri alınamaz. Ürünler taslağa alınır ve görselleri silinir.
                Sonrasında <strong>Watch Import</strong> menüsünden yeniden içe aktarmanız gerekir.
              </p>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-[#FAF8F3]">
          <button data-testid="gallery-modal-cancel" onClick={onCancel}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-border hover:bg-white">
            {isAudit ? "Kapat" : "İptal"}
          </button>
          {!isAudit && (
            <button data-testid="gallery-modal-confirm" onClick={onApply}
              disabled={busy || confirming || !preview || preview.will_clear_images === 0}
              className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-white disabled:opacity-40 inline-flex items-center gap-2 bg-[#DC2626]">
              {confirming ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Evet, Sıfırla ve Taslağa Al
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkStatusModal({ modal, onCancel, onApply }) {
  const { targetStatus, catLabel, preview, busy, confirming } = modal;
  const isPublish = targetStatus === "published";
  const headerColor = isPublish ? "#16A34A" : "#52525B";
  const Icon = isPublish ? Eye : EyeOff;
  const verbLong = isPublish ? "yayına alınacak" : "taslağa alınacak";
  const ctaLabel = isPublish ? "Evet, Tümünü Yayınla" : "Evet, Tümünü Taslağa Al";
  return (
    <div data-testid="bulk-status-modal" className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between" style={{ background: headerColor + "10" }}>
          <div className="flex items-center gap-2">
            <Icon size={16} style={{ color: headerColor }} />
            <h3 className="font-heading text-lg" style={{ color: headerColor }}>{isPublish ? "Tümünü Yayınla" : "Tümünü Taslağa Al"}</h3>
          </div>
          <button onClick={onCancel} className="p-1 hover:bg-white/40"><X size={16} /></button>
        </div>
        <div className="p-5 text-sm">
          <p className="text-[#52525B] mb-1">Kapsam</p>
          <p data-testid="bulk-status-scope" className="font-medium text-base mb-4">{catLabel}</p>
          {busy ? (
            <p className="text-[#71717A] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Etki hesaplanıyor…</p>
          ) : preview ? (
            <div className="space-y-2">
              <Row label="Kapsamdaki toplam ürün" value={preview.matched} />
              <Row label="Şu an Yayında" value={preview.published_now} muted />
              <Row label="Şu an Taslak" value={preview.draft_now} muted />
              <div className="border-t border-border pt-2 mt-2">
                <Row label={`${verbLong}`} value={preview.will_change} bold color={headerColor} testid="bulk-status-will-change" />
              </div>
              {preview.will_change === 0 && (
                <p className="text-xs text-[#71717A] italic mt-2">Hiçbir ürünün durumu değişmeyecek (zaten {isPublish ? "yayında" : "taslakta"}).</p>
              )}
            </div>
          ) : null}
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-[#FAF8F3]">
          <button data-testid="bulk-status-cancel" onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-[0.2em] border border-border hover:bg-white">İptal</button>
          <button data-testid="bulk-status-confirm" onClick={onApply} disabled={busy || confirming || !preview || preview.will_change === 0}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-white disabled:opacity-40 inline-flex items-center gap-2"
            style={{ background: headerColor }}>
            {confirming ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, muted, color, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-[#71717A]" : "text-[#52525B]"}>{label}</span>
      <span data-testid={testid} className={"font-finance " + (bold ? "text-lg font-medium" : "text-sm")} style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

// --- helpers (kept simple to avoid deep AST trees) ---

function brandOptionsFor(brands) {
  const out = [["", "Tüm Markalar"]];
  for (let i = 0; i < brands.length; i++) out.push([brands[i], brands[i]]);
  return out;
}

function stockFilterOpts() {
  const out = [["", "Tüm Stok"]];
  for (let i = 0; i < STOCK_OPTS.length; i++) out.push([STOCK_OPTS[i].v, STOCK_OPTS[i].l]);
  return out;
}

function buildTreeRows(childrenMap, expanded) {
  const rows = [];
  const stack = [];
  const roots = childrenMap["__root__"] || [];
  for (let i = roots.length - 1; i >= 0; i--) stack.push({ cat: roots[i], depth: 0 });
  while (stack.length) {
    const top = stack.pop();
    const kids = childrenMap[top.cat.id] || [];
    rows.push({ cat: top.cat, depth: top.depth, hasKids: kids.length > 0 });
    const isOpen = expanded[top.cat.id] !== false;
    if (isOpen) {
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ cat: kids[i], depth: top.depth + 1 });
    }
  }
  return rows;
}

function CatRow({ row, active, counts, expanded, setExpanded, onPick }) {
  const cat = row.cat;
  const isOpen = expanded[cat.id] !== false;
  const isActive = active === cat.id;
  const indent = 8 + row.depth * 12;
  const FolderIcon = isOpen && row.hasKids ? FolderOpen : Folder;
  const toggle = () => setExpanded({ ...expanded, [cat.id]: !isOpen });
  return (
    <div className={"flex items-center w-full hover:bg-white " + (isActive ? "bg-white" : "")} style={{ paddingLeft: indent }}>
      {row.hasKids ? (
        <button onClick={toggle} className="p-1 text-[#71717A]" data-testid={"cat-toggle-" + cat.slug}>
          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      ) : <span className="w-5" />}
      <button onClick={() => onPick(cat.id)} data-testid={"cat-pick-" + cat.slug}
        className={"flex items-center justify-between flex-1 px-1 py-1.5 text-xs text-left " + (isActive ? "text-[#C8A96A] font-medium" : "text-[#52525B] hover:text-[#0A0A0A]")}>
        <span className="flex items-center gap-1.5 truncate">
          <FolderIcon size={12} />
          {cat.name_tr}
        </span>
        <span className="text-[10px] text-[#71717A] ml-2">{counts[cat.id] || 0}</span>
      </button>
    </div>
  );
}

function SelectInput({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-[#C8A96A] bg-white">
      {options.map((opt) => <option key={opt[0]} value={opt[0]}>{opt[1]}</option>)}
    </select>
  );
}

function ProductRow({ p, categoryName, selected, onToggle, onEdit, onPatch }) {
  const img = p.images && p.images[0];
  const stock = STOCK_OPTS.find((s) => s.v === (p.stock_status || "in_stock")) || STOCK_OPTS[0];
  const status = STATUS_OPTS.find((s) => s.v === p.status) || STATUS_OPTS[1];
  const imgSrc = img ? (img.startsWith("http") ? img : process.env.REACT_APP_BACKEND_URL + img) : null;
  return (
    <tr data-testid={"prod-row-" + p.id} className={"border-b border-border hover:bg-[#FAF8F3] " + (selected ? "bg-[#FFFBEB]" : "")}>
      <td className="p-3"><input type="checkbox" checked={selected} onChange={onToggle} data-testid={"row-select-" + p.id} /></td>
      <td className="p-3">
        {imgSrc ? <img src={imgSrc} alt="" className="w-12 h-12 object-cover" /> : (
          <div className="w-12 h-12 bg-[#FAF8F3] flex items-center justify-center"><ImageIcon size={14} className="text-[#71717A]" /></div>
        )}
      </td>
      <td className="p-3 max-w-[320px]">
        <button onClick={onEdit} className="text-left hover:text-[#C8A96A]">
          <div className="font-medium truncate">{p.name_tr}</div>
          {p.brand ? <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{p.brand}</div> : null}
        </button>
      </td>
      <td className="p-3 font-mono text-[10px]">{p.sku}</td>
      <td className="p-3 text-[#52525B] truncate max-w-[220px]">{categoryName}</td>
      <td className="p-3 text-right"><InlineNumber value={p.price} onSave={(v) => onPatch(p.id, { price: v })} testid={"inline-price-" + p.id} format={(v) => formatTL(v)} /></td>
      <td className="p-3 text-center"><InlineNumber value={p.stock || 0} onSave={(v) => onPatch(p.id, { stock: parseInt(v) })} testid={"inline-stock-" + p.id} format={(v) => v} /></td>
      <td className="p-3"><span className={"text-[9px] uppercase tracking-wider text-white px-2 py-0.5 " + stock.c}>{stock.l}</span></td>
      <td className="p-3"><span className={"text-[9px] uppercase tracking-wider text-white px-2 py-0.5 " + status.c}>{status.l}</span></td>
      <td className="p-3 text-right">
        <button onClick={onEdit} className="p-1.5 text-[#52525B] hover:text-[#C8A96A]" data-testid={"row-edit-" + p.id}><Edit size={14} /></button>
      </td>
    </tr>
  );
}

function InlineNumber({ value, onSave, testid, format }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  React.useEffect(() => { setVal(value); }, [value]);
  const commit = () => {
    setEditing(false);
    if (parseFloat(val) !== parseFloat(value)) onSave(parseFloat(val) || 0);
  };
  if (!editing) {
    return (
      <button data-testid={testid} onClick={() => setEditing(true)}
        className="text-left hover:bg-[#FAF8F3] hover:text-[#C8A96A] px-1 py-0.5 cursor-text">
        {format(value)}
      </button>
    );
  }
  return (
    <input data-testid={testid + "-input"} type="number" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
      onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(value); setEditing(false); } }}
      className="w-24 border border-[#C8A96A] px-2 py-0.5 text-xs focus:outline-none" />
  );
}

const BULK_BUTTONS = [
  { tid: "bulk-publish", l: "Yayınla", cls: "bg-[#16A34A] text-white", action: "publish" },
  { tid: "bulk-unpublish", l: "Taslağa Al", cls: "bg-[#71717A] text-white", action: "unpublish" },
  { tid: "bulk-delete", l: "Sil", cls: "bg-[#EF4444] text-white", action: "delete" },
  { tid: "bulk-stock-status-in", l: "Stokta", cls: "bg-white border border-[#16A34A] text-[#16A34A]", action: "set_stock_status", extra: { stock_status: "in_stock" } },
  { tid: "bulk-stock-status-out", l: "Stokta Yok", cls: "bg-white border border-[#71717A]", action: "set_stock_status", extra: { stock_status: "out_of_stock" } },
  { tid: "bulk-stock-status-soon", l: "Çok Yakında", cls: "bg-white border border-[#C8A96A] text-[#C8A96A]", action: "set_stock_status", extra: { stock_status: "coming_soon" } },
  { tid: "bulk-featured", l: "Öne Çıkar", cls: "bg-white border border-[#C8A96A] text-[#C8A96A]", action: "set_flag", extra: { flag: "is_featured", value: true } },
  { tid: "bulk-new", l: "Yeni İşaretle", cls: "bg-white border border-[#C8A96A] text-[#C8A96A]", action: "set_flag", extra: { flag: "is_new", value: true } },
];

function BulkBar({ count, cats, onBulk, onClear }) {
  const [pct, setPct] = useState(10);
  const [stock, setStock] = useState(0);
  const [moveCat, setMoveCat] = useState("");
  return (
    <div data-testid="bulk-bar" className="px-4 py-2 bg-[#FFFBEB] border-b border-border flex items-center gap-2 flex-wrap text-xs">
      <span className="font-medium text-[#92400E]">{count} ürün seçili</span>
      {BULK_BUTTONS.map((b) => (
        <button key={b.tid} data-testid={b.tid} onClick={() => onBulk(b.action, b.extra)}
          className={b.cls + " px-3 py-1 uppercase tracking-wider"}>{b.l}</button>
      ))}
      <div className="flex items-center gap-1 bg-white border border-border px-2 py-1">
        <select value={moveCat} onChange={(e) => setMoveCat(e.target.value)} className="text-xs focus:outline-none" data-testid="bulk-move-cat">
          <option value="">Kategoriye Taşı…</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name_tr}</option>)}
        </select>
        <button onClick={() => moveCat && onBulk("category_change", { category_id: moveCat })} className="text-[#C8A96A] uppercase tracking-wider">Taşı</button>
      </div>
      <div className="flex items-center gap-1 bg-white border border-border px-2 py-1">
        <input type="number" value={pct} onChange={(e) => setPct(parseFloat(e.target.value) || 0)}
          className="w-12 text-center focus:outline-none" data-testid="bulk-pct" />
        <span>%</span>
        <button data-testid="bulk-price-up" onClick={() => onBulk("price_increase", { percent: pct })}
          className="text-[#16A34A] uppercase tracking-wider">+ Artır</button>
        <button data-testid="bulk-price-down" onClick={() => onBulk("price_decrease", { percent: pct })}
          className="text-[#EF4444] uppercase tracking-wider">- Azalt</button>
      </div>
      <div className="flex items-center gap-1 bg-white border border-border px-2 py-1">
        <span className="text-[#71717A]">Stok:</span>
        <input type="number" value={stock} onChange={(e) => setStock(parseInt(e.target.value) || 0)}
          className="w-14 text-center focus:outline-none" data-testid="bulk-stock-qty" />
        <button data-testid="bulk-stock" onClick={() => onBulk("set_stock", { stock })}
          className="text-[#0A0A0A] uppercase tracking-wider">Güncelle</button>
      </div>
      <button onClick={onClear} className="ml-auto text-[#71717A] underline">Seçimi Temizle</button>
    </div>
  );
}
